import { ReactElement } from 'react';

import { Avatar } from '@components/avatar';
import { IconFancyContainer } from '@components/icon-fancy-container';
import { IconApi } from '@components/icons/api';
import { IconCheckmark } from '@components/icons/checkmark';
import { IconForm } from '@components/icons/form';
import { IconHire } from '@components/icons/hire';
import { IconJourney } from '@components/icons/journey';
import { IconPause } from '@components/icons/pause';
import { IconPersonAdd } from '@components/icons/person-add';
import { IconPlay } from '@components/icons/play';
import { IconPortal } from '@components/icons/portal';
import { IconRestart } from '@components/icons/restart';
import { IconResume } from '@components/icons/resume';
import { IconWebhook } from '@components/icons/webhook';
import { Spinner } from '@components/loading-spinner';
import { Separator } from '@components/separator';
import { Horizontal, Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { abbreviateDate } from '@utils/date';

import { useJourneyActivityLogs } from 'api/traveller-journeys/hooks';
import { ActivityLogEntry, JourneyActivityEventType, TravellerSource } from 'api/types';

import {
  ConnectorLine,
  ContentColumn,
  EventConfig,
  ICON_SIZE,
  IconColumn,
  PerformedByRow,
} from './shared';

const EVENT_CONFIG: Record<JourneyActivityEventType, EventConfig> = {
  journey_started: { icon: <IconPlay size={ICON_SIZE} />, variant: 'blue' },
  journey_completed: { icon: <IconCheckmark size={ICON_SIZE} />, variant: 'green' },
  journey_continued: { icon: <IconResume size={ICON_SIZE} />, variant: 'green' },
  journey_paused: { icon: <IconPause size={ICON_SIZE} />, variant: 'orange' },
  journey_resumed: { icon: <IconResume size={ICON_SIZE} />, variant: 'green' },
  journey_reset: { icon: <IconRestart size={ICON_SIZE} />, variant: 'red' },
};

const EVENT_LABELS: Record<JourneyActivityEventType, keyof (typeof locale)['en']> = {
  journey_started: 'journeyStarted',
  journey_completed: 'journeyCompleted',
  journey_continued: 'journeyContinued',
  journey_paused: 'journeyPaused',
  journey_resumed: 'journeyResumed',
  journey_reset: 'journeyReset',
};

const DISPLAY_TO_SOURCE: Record<string, TravellerSource> = {
  'Manually added': 'manual',
  'External form': 'external_form',
  Webhook: 'webhook',
  'Scheduled polling': 'scheduled_polling',
  'Added from journey': 'added_from_journey',
  Hire: 'hire',
};

const SOURCE_ICONS: Record<TravellerSource, ReactElement> = {
  manual: <IconPersonAdd size={ICON_SIZE} />,
  webhook: <IconWebhook size={ICON_SIZE} />,
  scheduled_polling: <IconApi size={ICON_SIZE} />,
  external_form: <IconForm size={ICON_SIZE} />,
  added_from_journey: <IconJourney size={ICON_SIZE} />,
  hire: <IconHire size={ICON_SIZE} />,
  Hire: <IconHire size={ICON_SIZE} />,
  portal: <IconFancyContainer icon={<IconPortal size={ICON_SIZE} />} variant="indigo" size="sm" />,
  api: <IconApi size={ICON_SIZE} />,
};

const SOURCE_LABELS: Record<TravellerSource, string> = {
  manual: 'Manually added',
  webhook: 'Webhook',
  scheduled_polling: 'Scheduled polling',
  external_form: 'External form submission',
  added_from_journey: 'Added from another journey',
  hire: 'Hire',
  Hire: 'Hire',
  portal: 'Portal',
  api: 'API',
};

const groupByDate = (logs: ActivityLogEntry[]) => {
  const groups: { dateLabel: string; entries: ActivityLogEntry[] }[] = [];
  let currentKey = '';

  for (const log of logs) {
    const key = abbreviateDate(log.dateCreated, 'en', 'date');
    if (key !== currentKey) {
      currentKey = key;
      groups.push({
        dateLabel: abbreviateDate(log.dateCreated, 'en', 'date-fancy'),
        entries: [log],
      });
    } else {
      groups[groups.length - 1].entries.push(log);
    }
  }

  return groups;
};

export type JourneyActivityTimelineProps = {
  journeyId: string;
  source?: TravellerSource;
  sourceDescription?: string;
  sourceData?: { name?: string; email?: string; picture?: string };
  dateCreated?: string;
};

export const JourneyActivityTimeline = ({
  journeyId,
  source,
  sourceDescription,
  sourceData,
  dateCreated,
}: JourneyActivityTimelineProps) => {
  const { t } = useScopedTranslate(locale);
  const { data: logs, isLoading } = useJourneyActivityLogs(journeyId);

  if (isLoading) {
    return <Spinner />;
  }
  const sourceKey = (source ? DISPLAY_TO_SOURCE[source] ?? source : 'manual') as TravellerSource;
  const resolvedSourceLabel =
    sourceDescription || SOURCE_LABELS[sourceKey] || source || 'Manually added';
  const sourceIcon = SOURCE_ICONS[sourceKey] ?? <IconPersonAdd size={ICON_SIZE} />;

  const groups = groupByDate(logs ?? []);
  const sourceDateLabel = dateCreated ? abbreviateDate(dateCreated, 'en', 'date-fancy') : '';
  const firstGroupSameDay = groups.length > 0 && groups[0].dateLabel === sourceDateLabel;

  const hasLogs = groups.length > 0;

  const sourceEntry = dateCreated ? (
    <Horizontal space={3}>
      <IconColumn>
        <IconFancyContainer icon={sourceIcon} variant="indigo" size="sm" />
        {hasLogs && <ConnectorLine />}
      </IconColumn>
      <ContentColumn>
        <Text font="bold14" text={t('addedToJourney')} />
        {sourceData?.name ? (
          <PerformedByRow>
            <Avatar name={sourceData.name} src={sourceData.picture ?? undefined} size="sm" />
            <Text font="regular12" color="textPrimary" text={sourceData.name} />
          </PerformedByRow>
        ) : (
          <Text font="regular12" color="textPrimary" text={resolvedSourceLabel} />
        )}
        <Text
          font="regular12"
          color="textSecondary"
          text={abbreviateDate(dateCreated, 'en', 'datetime')}
        />
      </ContentColumn>
    </Horizontal>
  ) : null;

  return (
    <Stack space={4}>
      {dateCreated && !firstGroupSameDay && (
        <Stack space={4}>
          <Text font="semibold14" color="textPrimary" text={sourceDateLabel} />
          {sourceEntry}
        </Stack>
      )}
      {groups.map((group, groupIndex) => {
        const isFirstGroupWithSource = groupIndex === 0 && firstGroupSameDay;
        return (
          <Stack key={group.dateLabel} space={4}>
            {(groupIndex > 0 || (dateCreated && !firstGroupSameDay)) && <Separator />}
            <Text font="semibold14" color="textPrimary" text={group.dateLabel} />
            <Stack>
              {isFirstGroupWithSource && sourceEntry}
              {group.entries.map((log, entryIndex) => {
                const config = EVENT_CONFIG[log.eventType as JourneyActivityEventType];
                const isLast = entryIndex === group.entries.length - 1;
                return (
                  <Horizontal key={log.shortId} space={3}>
                    <IconColumn>
                      <IconFancyContainer icon={config.icon} variant={config.variant} size="sm" />
                      {!isLast && <ConnectorLine />}
                    </IconColumn>
                    <ContentColumn>
                      <Text
                        font="bold14"
                        text={t(EVENT_LABELS[log.eventType as JourneyActivityEventType])}
                      />
                      {log.performedBy && (
                        <PerformedByRow>
                          <Avatar
                            name={log.performedBy.name}
                            src={log.performedBy.picture ?? undefined}
                            size="sm"
                          />
                          <Text font="regular12" color="textPrimary" text={log.performedBy.name} />
                        </PerformedByRow>
                      )}
                      <Text
                        font="regular12"
                        color="textSecondary"
                        text={abbreviateDate(log.dateCreated, 'en', 'datetime')}
                      />
                    </ContentColumn>
                  </Horizontal>
                );
              })}
            </Stack>
          </Stack>
        );
      })}
      {!dateCreated && !hasLogs && (
        <Stack align="center" justify="center" style={{ padding: '2rem' }}>
          <Text font="regular14" color="textHelper" text={t('noActivity')} />
        </Stack>
      )}
    </Stack>
  );
};

const locale = {
  en: {
    addedToJourney: 'Added to journey',
    journeyStarted: 'Journey started',
    journeyCompleted: 'Journey completed',
    journeyContinued: 'Journey continued',
    journeyPaused: 'Journey paused',
    journeyResumed: 'Journey resumed',
    journeyReset: 'Journey reset',
    noActivity: 'No activity yet',
  },
};
